// src/components/NavBar.js
import * as React from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar, Box, Toolbar, Typography, Button, IconButton,
  Menu, MenuItem, Divider, Avatar, Stack, Chip,
} from "@mui/material";
import AccountCircleIcon    from "@mui/icons-material/AccountCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SchoolIcon           from "@mui/icons-material/School";
import BusinessIcon         from "@mui/icons-material/Business";
import LiveTvIcon           from "@mui/icons-material/LiveTv";
import { useAuth }          from "../auth/useAuth";
import { useTranslation }   from "react-i18next";
import LanguageMenu         from "./LanguageMenu";
import OrgSwitcher          from "./OrgSwitcher";

const ADMIN_ROUTES = (t) => [
  { path: "/admin/stats",               label: t("nav.adminstats",              { defaultValue: "Stats" }) },
  { path: "/admin/monitoring",          label: t("nav.admin_monitoring",        { defaultValue: "Monitoring" }) },
  { path: "/admin/labs",               label: t("nav.adminlabs",               { defaultValue: "Labs" }) },
  { path: "/admin/incidents",          label: t("nav.admin_incidents",         { defaultValue: "Incidents" }) },
  { path: "/admin/grading",            label: t("nav.admin_grading",           { defaultValue: "Examens" }) },
  { path: "/admin/recordings",         label: t("nav.admin_recordings",        { defaultValue: "Recordings" }) },
  { path: "/admin/lab-bundles",        label: t("nav.admin_lab_bundles",       { defaultValue: "Bundles" }) },
  { path: "/admin/credits",            label: t("nav.admin_credits",           { defaultValue: "Credits" }) },
  { path: "/admin/packages",           label: t("nav.admin_packages",          { defaultValue: "Packs" }) },
  { path: "/admin/users",              label: t("nav.admin_users",             { defaultValue: "Utilisateurs" }) },
  { path: "/admin/settings",           label: t("nav.admin_settings",          { defaultValue: "Parametres" }) },
  { path: "/admin/audit-logs",         label: t("nav.admin_audit_logs",        { defaultValue: "Audit" }) },
  { path: "/admin/gns3-nodes",         label: t("nav.admin_gns3_nodes",        { defaultValue: "ðŸ–§ GNS3 Nodes" }) },
];

const TEACHER_ROUTES = (t) => [
  { path: "/teacher/dashboard", label: t("nav.teacher_dashboard", { defaultValue: "Tableau de bord" }), icon: null },
  { path: "/teacher/live",      label: t("nav.teacher_live",      { defaultValue: "Sessions live" }),   icon: "live" },
  { path: "/teacher/grading",   label: t("nav.teacher_grading",   { defaultValue: "Examens" }),         icon: null },
  { path: "/teacher/students",  label: t("nav.teacher_students",  { defaultValue: "Etudiants" }),       icon: null },
];

const ORG_ROUTES = (t) => [
  { path: "/org/dashboard", label: t("nav.org_dashboard", { defaultValue: "Mon organisation" }) },
];

export default function NavBar() {
  const { t }                             = useTranslation();
  const navigate                          = useNavigate();
  const location                          = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  const isAdmin    = Boolean(user?.isAdmin);
  const isTeacher  = Boolean(user?.isTeacher && !user?.isAdmin);
  const isOrgAdmin = Boolean(user?.isOrgAdmin && !user?.isAdmin);

  const adminRoutes   = React.useMemo(() => ADMIN_ROUTES(t),   [t]);
  const teacherRoutes = React.useMemo(() => TEACHER_ROUTES(t), [t]);
  const orgRoutes     = React.useMemo(() => ORG_ROUTES(t),     [t]);

  const [anchorUser,    setAnchorUser]    = React.useState(null);
  const [anchorAdmin,   setAnchorAdmin]   = React.useState(null);
  const [anchorTeacher, setAnchorTeacher] = React.useState(null);
  const [anchorOrg,     setAnchorOrg]     = React.useState(null);

  const closeAll = () => {
    setAnchorUser(null);
    setAnchorAdmin(null);
    setAnchorTeacher(null);
    setAnchorOrg(null);
  };
  const go          = (path) => { closeAll(); navigate(path); };
  const handleLogout = () => { closeAll(); logout(); navigate("/login"); };

  const displayName = user?.full_name || user?.name || user?.username || user?.sub || "â€¦";
  const initials    = displayName.split(" ").filter(Boolean).map(w => w[0]||"").slice(0,2).join("").toUpperCase();
  const isActive    = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const navBtn = (path, label) => (
    <Button key={path} component={RouterLink} to={path} sx={{
      fontSize: 13, fontWeight: isActive(path) ? 700 : 500,
      color: isActive(path) ? "#6366f1" : "text.primary",
      borderRadius: 2, px: 1.5,
      bgcolor: isActive(path) ? "#eef2ff" : "transparent",
      "&:hover": { bgcolor: "#f1f5f9" },
    }}>
      {label}
    </Button>
  );

  const dropdownBtn = (label, anchor, setAnchor, activePath, color = "#6366f1") => (
    <Button
      endIcon={<KeyboardArrowDownIcon sx={{ fontSize: "16px !important" }} />}
      onClick={e => setAnchor(e.currentTarget)}
      sx={{
        fontSize: 13, fontWeight: location.pathname.startsWith(activePath) ? 700 : 500,
        color: location.pathname.startsWith(activePath) ? color : "text.primary",
        borderRadius: 2, px: 1.5,
        bgcolor: location.pathname.startsWith(activePath) ? color + "1a" : "transparent",
        "&:hover": { bgcolor: "#f1f5f9" },
      }}>
      {label}
    </Button>
  );

  const dropdownMenu = (anchor, routes, color = "#6366f1") => (
    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={closeAll}
      PaperProps={{ sx: { borderRadius: 2, border: "1px solid #e2e8f0", mt: 0.5, minWidth: 220 } }}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}>
      {routes.map(r => (
        <MenuItem key={r.path} onClick={() => go(r.path)}
          selected={isActive(r.path)}
          sx={{ fontSize: 13, borderRadius: 1, mx: 0.5, my: 0.25,
            "&.Mui-selected": { bgcolor: color + "1a", color, fontWeight: 700 } }}>
          {r.icon === "live" && (
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%",
              bgcolor: "#22c55e", display: "inline-block", mr: 1,
              animation: "pulse 1.5s ease-in-out infinite",
              "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: .3 } },
            }} />
          )}
          {r.label}
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <AppBar position="sticky" elevation={0} sx={{
      bgcolor: "white", borderBottom: "1px solid #e2e8f0", top: 0, zIndex: 1100,
    }}>
      <Toolbar sx={{ gap: 1, minHeight: "56px !important", px: { xs: 2, md: 3 } }}>

        {/* Logo */}
        <Stack direction="row" spacing={1} alignItems="center"
          component={RouterLink} to="/" sx={{ textDecoration: "none", mr: 2 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: "#6366f1",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Typography sx={{ color: "white", fontWeight: 800, fontSize: 14, lineHeight: 1 }}>N</Typography>
          </Box>
          <Typography variant="subtitle1" fontWeight={800}
            sx={{ color: "text.primary", letterSpacing: -0.3 }}>
            NextHop
          </Typography>
        </Stack>

        {/* Nav principale */}
        <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.5, alignItems: "center" }}>

          {/* Public */}
          {navBtn("/",    t("nav.home",  { defaultValue: "Accueil" }))}
          {navBtn("/labs", t("nav.labs", { defaultValue: "Labs" }))}

          {isAuthenticated && (
            <>
              {/* Apprenant */}
              {navBtn("/dashboard",    t("nav.dashboard",    { defaultValue: "Dashboard" }))}
              {navBtn("/reservations", t("nav.reservations", { defaultValue: "Reservations" }))}
              {navBtn("/me/stats",     t("nav.userstats",    { defaultValue: "Mes stats" }))}

              {/* Teacher menu */}
              {(isTeacher || user?.isAdmin) && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
                  {dropdownBtn(
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <SchoolIcon sx={{ fontSize: 15 }} />
                      <span>{t("nav.teacher_menu", { defaultValue: "Enseignant" })}</span>
                    </Stack>,
                    anchorTeacher, setAnchorTeacher, "/teacher", "#0f766e"
                  )}
                  {dropdownMenu(anchorTeacher, teacherRoutes, "#0f766e")}
                </>
              )}

              {/* Org admin menu */}
              {isOrgAdmin && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
                  {dropdownBtn(
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <BusinessIcon sx={{ fontSize: 15 }} />
                      <span>{t("nav.org_menu", { defaultValue: "Organisation" })}</span>
                    </Stack>,
                    anchorOrg, setAnchorOrg, "/org", "#b45309"
                  )}
                  {dropdownMenu(anchorOrg, orgRoutes, "#b45309")}
                </>
              )}

              {/* Admin menu */}
              {isAdmin && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
                  {dropdownBtn(
                    t("nav.admin_menu", { defaultValue: "Admin" }),
                    anchorAdmin, setAnchorAdmin, "/admin", "#6366f1"
                  )}
                  <Menu anchorEl={anchorAdmin} open={Boolean(anchorAdmin)} onClose={closeAll}
                    PaperProps={{ sx: { borderRadius: 2, border: "1px solid #e2e8f0", mt: 0.5, minWidth: 220 } }}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}>
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography variant="caption" color="text.disabled" fontWeight={600}
                        sx={{ textTransform: "uppercase", letterSpacing: .5 }}>
                        {t("nav.admin_section", { defaultValue: "Administration" })}
                      </Typography>
                    </Box>
                    <Divider />
                    {adminRoutes.map(r => (
                      <MenuItem key={r.path} onClick={() => go(r.path)}
                        selected={isActive(r.path)}
                        sx={{ fontSize: 13, borderRadius: 1, mx: 0.5, my: 0.25,
                          "&.Mui-selected": { bgcolor: "#eef2ff", color: "#6366f1", fontWeight: 700 } }}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </>
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* OrgSwitcher (super_admin) */}
        {isAuthenticated && user?.isSuperAdmin && <OrgSwitcher />}

        {/* Langue */}
        <LanguageMenu compact />

        {/* Auth */}
        {!isAuthenticated ? (
          <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
            <Button component={RouterLink} to="/login" variant="outlined" size="small"
              sx={{ borderRadius: 2, fontSize: 13 }}>
              {t("nav.login", { defaultValue: "Connexion" })}
            </Button>
            <Button component="a" href="https://ecourses.nexthopacademy.net/inscription" variant="contained" size="small"
              sx={{ borderRadius: 2, fontSize: 13, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
              {t("nav.register", { defaultValue: "Inscription" })}
            </Button>
          </Stack>
        ) : (
          <>
            <IconButton onClick={e => setAnchorUser(e.currentTarget)} size="small" sx={{ ml: 1 }}>
              <Avatar sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 700, bgcolor: "#6366f1" }}>
                {initials || <AccountCircleIcon fontSize="small" />}
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchorUser} open={Boolean(anchorUser)} onClose={closeAll}
              PaperProps={{ sx: { borderRadius: 2, border: "1px solid #e2e8f0", mt: 0.5, minWidth: 220 } }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>{displayName}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                {user?.role && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={user.role} size="small"
                      sx={{ fontSize: 10, height: 18,
                        bgcolor: user.isAdmin ? "#eef2ff" : user.isTeacher ? "#f0fdf4" : "#f8fafc",
                        color:   user.isAdmin ? "#4f46e5" : user.isTeacher ? "#0f766e" : "#64748b",
                      }} />
                  </Box>
                )}
              </Box>
              <Divider />
              <MenuItem onClick={() => go("/profile")}    sx={{ fontSize: 13 }}>
                {t("nav.profile",   { defaultValue: "Profil" })}
              </MenuItem>
              <MenuItem onClick={() => go("/dashboard")}  sx={{ fontSize: 13 }}>
                {t("nav.dashboard", { defaultValue: "Dashboard" })}
              </MenuItem>
              {user?.isTeacher && (
                <MenuItem onClick={() => go("/teacher/dashboard")} sx={{ fontSize: 13, color: "#0f766e" }}>
                  <SchoolIcon sx={{ fontSize: 14, mr: 1 }} />
                  {t("nav.teacher_dashboard", { defaultValue: "Espace enseignant" })}
                </MenuItem>
              )}
              {user?.isOrgAdmin && !user?.isAdmin && (
                <MenuItem onClick={() => go("/org/dashboard")} sx={{ fontSize: 13, color: "#b45309" }}>
                  <BusinessIcon sx={{ fontSize: 14, mr: 1 }} />
                  {t("nav.org_dashboard", { defaultValue: "Mon organisation" })}
                </MenuItem>
              )}
              <MenuItem onClick={() => go("/me/credits")} sx={{ fontSize: 13 }}>
                {t("nav.credits", { defaultValue: "Mes credits" })}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ fontSize: 13, color: "#ef4444" }}>
                {t("nav.logout", { defaultValue: "Deconnexion" })}
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

